import React, { useState, useRef, useEffect } from 'react';
import { DayPicker } from 'react-day-picker';
import 'react-day-picker/dist/style.css';
import useFormatDatePicker from '../../hooks/useFormatDatePicker';
import moment from 'moment';
import classNames from '@app/ui/sports-components/utils/classNames';

interface MvcDatePickerProps {
    value?: Date;
    minDate?: Date;
    maxDate?: Date;
    monthInCalendar?: Date;
    onDateSelectionChange: (date: Date | undefined) => void;
    onMonthInCalendarChange?: (date: Date) => void;
    limitYear?: (currentYear: number) => number;
    inputProps?: any;
    className?: string;
    style?: React.CSSProperties;
    size?: 'xs' | 'sm' | 'md' | 'lg';
}

const MIN_DATE = new Date(2018, 0, 1); // January 1st, 2018
const MAX_DATE = new Date();

const MvcDatePicker: React.FC<MvcDatePickerProps> = ({
    value,
    minDate = MIN_DATE,
    maxDate = MAX_DATE,
    monthInCalendar,
    onMonthInCalendarChange,
    onDateSelectionChange,
    size = 'sm',
    ...props
}) => {
    const { format, formatDate, CURRENT_YEAR, parseDate, locale } =
        useFormatDatePicker();

    const [selectedDay, setSelectedDay] = useState<Date | undefined>(value);
    const [inputValue, setInputValue] = useState(
        value ? formatDate(value) : '',
    );
    const [showDayPicker, setShowDayPicker] = useState(false);
    const [currentMonth, setCurrentMonth] = useState<Date>(
        monthInCalendar || value || new Date(),
    );
    const datePickerRef = useRef<HTMLDivElement>(null);

    const isDateValid = (date: Date | string): boolean => {
        // Ensure the format string is in uppercase following Moment.js conventions
        const upperCaseFormat = format.toUpperCase();

        // Parse the date based on its type
        const momentDate =
            typeof date === 'string'
                ? moment(date, upperCaseFormat, true) // Strict parsing for strings
                : moment(date); // Directly use Moment.js for Date objects

        return (
            momentDate.isValid() &&
            (!minDate || momentDate.isSameOrAfter(minDate, 'day')) &&
            (!maxDate || momentDate.isSameOrBefore(maxDate, 'day'))
        );
    };

    const handleDayClick = (date: Date) => {
        const isValidDate = isDateValid(date);
        if (isValidDate) {
            setSelectedDay(date);
            setInputValue(formatDate(date));
            onDateSelectionChange(date);
        } else {
            clearSelection();
        }
        setShowDayPicker(false);
    };

    const handleMonthChange = (month: Date) => {
        setCurrentMonth(month);
        onMonthInCalendarChange?.(month);
    };

    const clearSelection = () => {
        setSelectedDay(undefined);
        setInputValue('');
        onDateSelectionChange(undefined);
    };

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                datePickerRef.current &&
                !datePickerRef.current.contains(event.target as Node)
            ) {
                setShowDayPicker(false);
                if (!isDateValid(selectedDay)) {
                    clearSelection();
                } else {
                    setCurrentMonth(selectedDay);
                }
                if (!isDateValid(inputValue)) {
                    setInputValue('');
                }
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [selectedDay, minDate, maxDate, inputValue]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setInputValue(value);
        const date = parseDate(value);

        if (date && isDateValid(date)) {
            setSelectedDay(date);
            onDateSelectionChange(date);
            handleMonthChange(date);
        }
    };

    const inputClass = classNames(
        `gds-form-group__text-input--${size}`,
        'gds-form-group__text-input',
    );

    return (
        <div
            className="datepicker-container"
            ref={datePickerRef}
            style={{ position: 'relative' }}
        >
            <input
                type="text"
                className={inputClass}
                value={inputValue}
                placeholder={format}
                onChange={handleInputChange}
                onFocus={() => setShowDayPicker(true)}
                {...props.inputProps}
            />
            {showDayPicker && (
                <div
                    className="datepicker-popup"
                    style={{
                        position: 'absolute',
                        top: '100%',
                        right: 0,
                        zIndex: 1000,
                        backgroundColor: 'white',
                        boxShadow: '0 2px 5px rgba(0, 0, 0, 0.15)',
                    }}
                >
                    <DayPicker
                        selected={selectedDay}
                        onDayClick={handleDayClick}
                        month={currentMonth}
                        onMonthChange={handleMonthChange}
                        fromYear={2018}
                        toYear={CURRENT_YEAR + 1}
                        locale={locale}
                        captionLayout="dropdown-buttons"
                        disabled={[{ after: maxDate }, { before: minDate }]}
                    />
                </div>
            )}
        </div>
    );
};

export default MvcDatePicker;
